import React, { Suspense, useEffect, useRef, useState } from "react";
import { ThemeProvider } from "@/hooks/useTheme";
import { Toaster as Sonner } from "@/components/ui/sonner-toaster";
import RouteHistoryTracker from '@/components/RouteHistoryTracker';
import ScrollToTop from '@/components/ScrollToTop';
import { useGoogleOneTap } from '@/hooks/useGoogleOneTap';
// QueryClientProvider is provided at the root in main.tsx
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import SecurityHeaders from "@/components/SecurityHeaders";
import PrivateConsoleSecurityBanner from '@/components/PrivateConsoleSecurityBanner';
import LiveNotificationToasts from '@/features/notifications/LiveNotificationToasts';
import { installRealtimeSoundUnlock } from '@/features/realtime/realtime-sounds';
import {
  ADMIN_ROUTE_PREFIX,
  AGENT_ROUTE_PREFIX,
  buildAdminRoute,
  isSecureAdminPath,
} from '@/constants/security';
import {
  LEGACY_PUBLIC_LISTING_ROUTE_PATTERNS,
  PUBLIC_LISTING_ROUTE_PATTERNS,
  PUBLIC_PROPERTY_DETAIL_ROUTE_PATTERNS,
} from '@/routes/publicPropertyRoutes';
import {
  buildOwnerRegistrationHref,
  LEGACY_OWNER_REGISTRATION_ROUTE,
  OWNER_LANDING_ROUTE,
  OWNER_REGISTRATION_ROUTE,
} from '@/lib/ownerRoutes';
const Index = React.lazy(() => import('./pages/Index'));
const PropiedadesSplitView = React.lazy(() => import('./pages/PropiedadesSplitView'));
const OwnerLandingPage = React.lazy(() => import('./pages/OwnerLandingPage'));
const OwnerRegistrationPage = React.lazy(() => import('./pages/public/OwnerRegistrationPage'));
const PropertiesFavoritePage = React.lazy(() => import('./pages/public/PropertiesFavoritePage'));

const PublicPropertyDetailPage = React.lazy(() => import('./pages/PublicPropertyDetailPage'));
const AdminProtectedRoute = React.lazy(() => import('./components/AdminProtectedRoute'));
const AgentProtectedRoute = React.lazy(() => import('./components/AgentProtectedRoute'));
const InternalOperatorProtectedRoute = React.lazy(() => import('./components/InternalOperatorProtectedRoute'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const UnauthorizedPage = React.lazy(() => import('@/components/Unauthorized'));
const PropertyFormPage = React.lazy(() => import('./components/admin/PropertyFormPage'));
import { HelmetProvider } from 'react-helmet-async';
const TerminosInquilinos = React.lazy(() => import('./components/TerminosCondiciones'));

const VisitConfirmationUser = React.lazy(() => import('@/pages/public/VisitConfirmationUser'));
const UpdatePassword = React.lazy(() => import('./routes/UpdatePassword'));
const RequestPasswordReset = React.lazy(() => import('./pages/RequestPasswordReset'));
const PublicLogin = React.lazy(() => import('./pages/PublicLogin'));

const PublicLoginRouteShell = React.memo(function PublicLoginRouteShell() {
  const rc = useRef(0);
  rc.current++;
  if (isAuthDebugEnabled()) {
    authDebug('PublicLoginRouteElement.render', { n: rc.current });
  }
  return (
    <Suspense fallback={<RouteFallbackMountLogger route="login">{<PublicAuthRouteSkeleton />}</RouteFallbackMountLogger>}>
      <PublicLogin />
    </Suspense>
  );
});

/** Referencia estable al wrapper memoizado (evita remount por identity del elemento Route). */
const publicLoginRouteElement = <PublicLoginRouteShell />;
const PublicRegister = React.lazy(() => import('./pages/PublicRegister'));
const AuthCallback = React.lazy(() => import('./routes/AuthCallback'));
const RestrictedAccess = React.lazy(() => import('./pages/RestrictedAccess'));
const PoliticaPrivacidad = React.lazy(() => import('./components/PoliticaPrivacidad'));
const AvisoSeguridad = React.lazy(() => import('./components/AvisoSeguridad'));
const AgendarVisitaDatos = React.lazy(() => import('./pages/public/VisitorRegistrationPage'));
import MobileFooterNav from '@/components/MobileFooterNav';
import { PublicAuthenticatedWhatsappSuggestion } from '@/components/public/PublicAuthenticatedWhatsappSuggestion';
import { AuthDebugOverlayGate } from '@/components/dev/AuthDebugOverlay';
import { authDebug, isAuthDebugEnabled } from '@/lib/authDebugInstrumentation';
const TenantLeaseProcess = React.lazy(() => import("./pages/public/TenantLeaseProcess"));
const TenantLeaseSectionPage = React.lazy(() => import('./pages/public/TenantLeaseSectionPage'));
const PrivacyPolicySectionPage = React.lazy(() => import('./pages/public/PrivacyPolicySectionPage'));
import {
  HomeRouteSkeleton,
  OwnerLandingRouteSkeleton,
  PrivateRouteSkeleton,
  DashboardTabSkeleton,
  PublicAuthRouteSkeleton,
  PublicContentRouteSkeleton,
  PublicPropertiesRouteSkeleton,
  PublicPropertyDetailRouteSkeleton,
} from '@/components/loading/route-skeletons';
import { PropertyDetailInternalSkeleton } from '@/Dashboard/Admin/Properties/skeleton/PropertyDetailInternalSkeleton';
const OwnerGuideSection = React.lazy(() => import("./pages/public/OwnerGuideSection"));
const OwnerGuideSectionPage = React.lazy(() => import('./pages/public/OwnerGuideSectionPage'));
const PropertyDetailInternal = React.lazy(() => import('@/Dashboard/Admin/Properties/PropertyDetailInternal'));
const SettingsPanel = React.lazy(() => import('./components/Dashboard/Settings/SettingsPanel'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const ClientsDahboard = React.lazy(() => import("@/Dashboard/Admin/Clients/ClientsDahboard"));
const AcquisitionsManagerPage = React.lazy(() => import('@/Dashboard/Admin/Acquisitions/AcquisitionsManagerPage'));
const AcquisitionsManagerPageSkeleton = React.lazy(() => import('@/Dashboard/Admin/Acquisitions/AcquisitionsManagerPageSkeleton'));
const AgentDashboard = React.lazy(() => import('./pages/AgentDashboard'));
const AgentOverviewPage = React.lazy(() => import('./pages/agent/AgentOverviewPage'));
const AgentClientsPage = React.lazy(() => import('./Dashboard/Agent/Clients/ClientsPage'));
const AgentPropertiesPage = React.lazy(() => import('./Dashboard/Agent/Properties/PropertiesPage'));
const AgentPropertyDetailPage = React.lazy(() => import('./Dashboard/Agent/Properties/PropertyDetailPage'));
const TeamOperatorProfile = React.lazy(() => import('@/Dashboard/Team/TeamOperatorProfile'));
const TeamManagementDashboard = React.lazy(() => import('@/Dashboard/Team/TeamManagementOperators'));
const SuperAdminProtectedRoute = React.lazy(() => import('@/components/SuperAdminProtectedRoute'));

const AgentDashboardRouteElement: React.FC = () => {
  const location = useLocation();

  return (
    <ThemeProvider scope="local">
      <Suspense fallback={<PrivateRouteSkeleton />}>
        <AgentProtectedRoute>
          <AgentDashboard key={location.pathname} />
        </AgentProtectedRoute>
      </Suspense>
    </ThemeProvider>
  );
};

function RoutesInstrumentationShell({
   children }: Readonly<{ children: React.ReactNode }>
  ) {
  const location = useLocation();
  const renderRef = useRef(0);
  renderRef.current++;

  useEffect(() => {
    if (!isAuthDebugEnabled()) return;
    authDebug('App.routes_render', {
      pathname: location.pathname,
      searchLenApprox: location.search?.length ?? 0,
    });
  }, [location.pathname, location.search]);

  if (isAuthDebugEnabled()) {
    authDebug('App.location_snapshot', {
      pathname: location.pathname,
      searchLenApprox: location.search?.length ?? 0,
      renderN: renderRef.current,
    });
  }

  return <>{children}</>;
}

function RouteFallbackMountLogger({
   children, route }: Readonly<{ children: React.ReactNode; route?: string }>
  ) {
  useEffect(() => {
    authDebug('RouteFallback.mount', { route: route ?? 'unknown' });
    return () => {
      authDebug('RouteFallback.unmount', { route: route ?? 'unknown' });
    };
  }, [route]);

  return <>{children}</>;
}

const PUBLIC_ONE_TAP_PATHS = new Set([
  '/',
  '/login',
  '/register',
  '/request-password-reset',
  '/update-password',
]);

function scheduleDeferredMount(callback: () => void, timeout: number) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  if ('requestIdleCallback' in window) {
    const idleId = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback(idleId);
  }

  const timerId = globalThis.setTimeout(callback, Math.min(timeout, 1200));
  return () => globalThis.clearTimeout(timerId);
}

function withRouteFallback(element: React.ReactNode, fallback: React.ReactNode) {
  return <Suspense fallback={<RouteFallbackMountLogger>{fallback}</RouteFallbackMountLogger>}>{element}</Suspense>;
}

// Redirect helper to preserve query params when moving legacy paths
const LegacyOwnerFormRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={buildOwnerRegistrationHref(location.search, location.hash)} replace />;
};

const LegacyOwnerRegistrationRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={buildOwnerRegistrationHref(location.search, location.hash)} replace />;
};

const LegacyPublicListingRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/renta${location.search || ''}${location.hash || ''}`} replace />;
};


// Limpia la clase global 'dark' al entrar a rutas públicas (Desactivado para habilitar Dark Mode en Landing)
const ClearGlobalDarkOnPublic = () => {
  // Retenido temporalmente como no-op para evitar romper imports o lógica posterior.
  // Ahora permitimos que la página pública fluya con el tema oscuro global.
  return null;
};

const RealtimeSoundBootstrap: React.FC = () => {
  useEffect(() => {
    const cleanup = installRealtimeSoundUnlock();
    return typeof cleanup === 'function' ? cleanup : undefined;
  }, []);

  return null;
};

const DeferredGlobalEnhancements: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();
  const [deferredReady, setDeferredReady] = useState(false);

  useEffect(() => {
    setDeferredReady(false);

    if (loading) {
      return;
    }

    return scheduleDeferredMount(() => setDeferredReady(true), 1800);
  }, [loading, location.pathname]);

  const isPrivateRoute = isSecureAdminPath(location.pathname) || location.pathname.startsWith('/agente');
  const shouldMountRealtime = deferredReady && !loading && isAuthenticated;
  const shouldMountGoogleOneTap = deferredReady && !loading && !isAuthenticated && PUBLIC_ONE_TAP_PATHS.has(location.pathname);
  const shouldMountPublicSuggestion = deferredReady && !loading && isAuthenticated && !isPrivateRoute;

  return (
    <>
      {shouldMountRealtime && isPrivateRoute ? <RealtimeSoundBootstrap /> : null}
      {shouldMountPublicSuggestion ? <PublicAuthenticatedWhatsappSuggestion /> : null}
      {shouldMountRealtime && isPrivateRoute ? <LiveNotificationToasts /> : null}
      {shouldMountGoogleOneTap ? <GoogleOneTapHost /> : null}
    </>
  );
};

function App() {
  const appRenderRef = useRef(0);
  appRenderRef.current++;
  if (isAuthDebugEnabled()) {
    authDebug('App.render', { n: appRenderRef.current });
  }

  return (
    <AuthProvider>
      <ThemeProvider scope="global">
        <HelmetProvider>
          <SecurityHeaders />
    <Sonner />
    <BrowserRouter>
          <ClearGlobalDarkOnPublic />
          <PrivateConsoleSecurityBanner />
          <RouteHistoryTracker />
          <ScrollToTop />
          <AuthDebugOverlayGate />
          <DeferredGlobalEnhancements />
          {/*
            NavigationDiagnostics quedó oculto por ahora.
            Función: mostrar un panel dev-only con tiempos de transición de ruta,
            último intento de navegación y censo de overlays/portales visibles.
            Uso futuro: volver a importarlo aquí y reactivar esta línea cuando
            reaparezcan bugs de navegación, clicks tragados o capas bloqueantes.
          */}
          {/* {import.meta.env.DEV ? <NavigationDiagnostics /> : null} */}
      <RoutesInstrumentationShell>
          <>
          <Routes>
            <Route path="/admin/*" element={withRouteFallback(<NotFound />, <PublicContentRouteSkeleton />)} />
            <Route path={buildAdminRoute('tasks/status-guide')} element={withRouteFallback(<NotFound />, <PublicContentRouteSkeleton />)} />
            <Route path="/register" element={withRouteFallback(<PublicRegister />, <PublicAuthRouteSkeleton />)} />
            <Route path="/" element={withRouteFallback(<Index />, <HomeRouteSkeleton />)} />
           <Route path="/selecciona-perfil" element={<Navigate to="/register" replace />} />
            <Route path="/publicar-mi-inmueble" element={<LegacyOwnerFormRedirect />} />
            <Route path={LEGACY_OWNER_REGISTRATION_ROUTE} element={<LegacyOwnerRegistrationRedirect />} />
            <Route path={OWNER_LANDING_ROUTE} element={withRouteFallback(<OwnerLandingPage />, <OwnerLandingRouteSkeleton />)} />
            <Route path={OWNER_REGISTRATION_ROUTE} element={withRouteFallback(<OwnerRegistrationPage />, <PublicContentRouteSkeleton />)} />
              {PUBLIC_LISTING_ROUTE_PATTERNS.map((path) => (
                <Route key={path} path={path} element={withRouteFallback(<PropiedadesSplitView />, <PublicPropertiesRouteSkeleton />)} />
              ))}
              {LEGACY_PUBLIC_LISTING_ROUTE_PATTERNS.map((path) => (
                <Route key={path} path={path} element={<LegacyPublicListingRedirect />} />
              ))}
              {PUBLIC_PROPERTY_DETAIL_ROUTE_PATTERNS.map((path) => (
                <Route key={path} path={path} element={withRouteFallback(<PublicPropertyDetailPage />, <PublicPropertyDetailRouteSkeleton />)} />
              ))}
            <Route path="/propiedades/:slug" element={withRouteFallback(<PublicPropertyDetailPage />, <PublicPropertyDetailRouteSkeleton />)} />
            <Route path="/mi-visita/:visitPublicId" element={withRouteFallback(<VisitConfirmationUser />, <PublicContentRouteSkeleton />)} />
            <Route path="/terminos-y-condiciones" element={withRouteFallback(<TerminosInquilinos />, <PublicContentRouteSkeleton />)} />
            <Route path="/terminos-condiciones" element={withRouteFallback(<TerminosInquilinos />, <PublicContentRouteSkeleton />)} />
            <Route path="/terminos-inquilinos" element={withRouteFallback(<TerminosInquilinos />, <PublicContentRouteSkeleton />)} />
            <Route path="/update-password" element={withRouteFallback(<UpdatePassword />, <PublicAuthRouteSkeleton />)} />
            <Route path="/agendar/datos" element={withRouteFallback(<AgendarVisitaDatos />, <PublicContentRouteSkeleton />)} />
            <Route path="/request-password-reset" element={withRouteFallback(<RequestPasswordReset />, <PublicAuthRouteSkeleton />)} />
            <Route path="/favoritos" element={withRouteFallback(<PropertiesFavoritePage />, <PublicPropertiesRouteSkeleton />)} />
            <Route path="/login" element={publicLoginRouteElement} />
            <Route path="/acceso-restringido" element={withRouteFallback(<RestrictedAccess />, <PublicAuthRouteSkeleton />)} />
            <Route path="/auth/callback" element={withRouteFallback(<AuthCallback />, <PublicAuthRouteSkeleton />)} />
            <Route path="/requisitos-arrendatarios" element={withRouteFallback(<TenantLeaseProcess />, <PublicContentRouteSkeleton />)} />
            <Route path="/requisitos-arrendatarios/:sectionId" element={withRouteFallback(<TenantLeaseSectionPage />, <PublicContentRouteSkeleton />)} />
            <Route path="/politica-privacidad" element={withRouteFallback(<PoliticaPrivacidad />, <PublicContentRouteSkeleton />)} />
            <Route path="/politica-privacidad/:sectionId" element={withRouteFallback(<PrivacyPolicySectionPage />, <PublicContentRouteSkeleton />)} />
            <Route path="/aviso-de-seguridad" element={withRouteFallback(<AvisoSeguridad />, <PublicContentRouteSkeleton />)} />
            <Route path="/guia-del-propietario" element={withRouteFallback(<OwnerGuideSection />, <PublicContentRouteSkeleton />)} />
            <Route path="/guia-del-propietario/:sectionId" element={withRouteFallback(<OwnerGuideSectionPage />, <PublicContentRouteSkeleton />)} />
      <Route 
            path={AGENT_ROUTE_PREFIX}
            element={<AgentDashboardRouteElement />}
          >
            <Route index element={<Suspense fallback={<DashboardTabSkeleton />}><AgentOverviewPage /></Suspense>} />
            <Route path="clientes" element={<Suspense fallback={<DashboardTabSkeleton />}><AgentClientsPage /></Suspense>} />
            <Route path="propiedades" element={<Suspense fallback={<DashboardTabSkeleton />}><AgentPropertiesPage /></Suspense>} />
            <Route path="propiedades/nueva" element={<Suspense fallback={<DashboardTabSkeleton />}><PropertyFormPage context="agent" /></Suspense>} />
            <Route path="propiedades/editar/:id" element={<Suspense fallback={<DashboardTabSkeleton />}><PropertyFormPage context="agent" /></Suspense>} />
            <Route path="borradores" element={<Suspense fallback={<DashboardTabSkeleton />}><AgentPropertiesPage scope="drafts" /></Suspense>} />
            <Route path="propiedades/:listingCode" element={<Suspense fallback={<DashboardTabSkeleton />}><AgentPropertyDetailPage /></Suspense>} />
          </Route>
          <Route
              path={`${ADMIN_ROUTE_PREFIX}/*`} 
              element={
        <ThemeProvider scope="local">
                  <Suspense fallback={<PrivateRouteSkeleton />}>
                    <AdminProtectedRoute>
                      <AdminDashboard />
                    </AdminProtectedRoute>
                  </Suspense>
                </ThemeProvider>
              }
            >
              <Route
                path="configuracion"
                element={
                  <Suspense fallback={<DashboardTabSkeleton />}>
                    <SettingsPanel />
                  </Suspense>
                }
              />
              <Route
                path="clients"
                element={
                  <Suspense fallback={<DashboardTabSkeleton />}>
                    <ClientsDahboard />
                  </Suspense>
                }
              />
              <Route
                path="adquisiciones"
                element={
                  <Suspense fallback={<AcquisitionsManagerPageSkeleton />}>
                    <AcquisitionsManagerPage />
                  </Suspense>
                }
              />
              <Route
                path="clients/team/:actorId"
                element={
                  <Suspense fallback={<DashboardTabSkeleton />}>
                    <TeamOperatorProfile />
                  </Suspense>
                }
              />

            </Route>
          <Route
            path={`${ADMIN_ROUTE_PREFIX}/team-management`}
            element={
              <ThemeProvider scope="local">
                <Suspense fallback={<PrivateRouteSkeleton />}>
                  <AdminProtectedRoute>
                    <SuperAdminProtectedRoute>
                      <TeamManagementDashboard />
                    </SuperAdminProtectedRoute>
                  </AdminProtectedRoute>
                </Suspense>
              </ThemeProvider>
            }
          />
          {/* Ruta standalone de detalle de reembolso - fullscreen sin AdminSidebar */}
          <Route
            path={`${ADMIN_ROUTE_PREFIX}/detalle-interno/:slug/:public_id`}
            element={
              <ThemeProvider scope="local">
                <Suspense fallback={<PropertyDetailInternalSkeleton />}>
                  <InternalOperatorProtectedRoute>
                    <PropertyDetailInternal />
                  </InternalOperatorProtectedRoute>
                </Suspense>
              </ThemeProvider>
            }
          />
          <Route
            path={`${ADMIN_ROUTE_PREFIX}/properties/new`}
            element={
              <ThemeProvider scope="local">
                <Suspense fallback={<PrivateRouteSkeleton />}>
                  <AdminProtectedRoute>
                    <PropertyFormPage context="admin" />
                  </AdminProtectedRoute>
                </Suspense>
              </ThemeProvider>
            }
          />
          <Route
            path={`${ADMIN_ROUTE_PREFIX}/properties/edit/:id`}
            element={
              <ThemeProvider scope="local">
                <Suspense fallback={<PrivateRouteSkeleton />}>
                  <AdminProtectedRoute>
                    <PropertyFormPage context="admin" />
                  </AdminProtectedRoute>
                </Suspense>
              </ThemeProvider>
            }
          />
          <Route path="unauthorized" element={withRouteFallback(<UnauthorizedPage />, <PublicContentRouteSkeleton />)} />
          {/* 404 Route */}
          <Route path="*" element={withRouteFallback(<NotFound />, <PublicContentRouteSkeleton />)} />
          </Routes>
          {/* Global sticky mobile footer navigation (hidden on private routes) */}
          <MobileFooterNav />
          </>
      </RoutesInstrumentationShell>
        </BrowserRouter>
        </HelmetProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;

// Mounts Google One Tap prompt globally on public pages and renders a fallback button container
const GoogleOneTapHost: React.FC = () => {
  // Desactiva en áreas privadas/admin para evitar fricción visual
  const location = useLocation();
  const isPrivate = isSecureAdminPath(location.pathname)
    || location.pathname.startsWith('/agente');

  useGoogleOneTap({ enabled: !isPrivate });
  // Ocultar el botón flotante en móviles para no tapar el footer; sólo visible en md+
  return <div id="gsi-btn" className="hidden md:block fixed bottom-5 right-5 z-[60]" />;
};
